import {
    RelationOptions,
    Experiment,
    Attribute,
    getRelationOptionDisplayName,
} from "@/services/things/models";
import { parseNumber } from "@/lib/numberUtils";

import _ from "lodash";
import { AbstractSchemaConfigWithGlobalRelationOptions } from "../schemas/manageSchemasAPI";
import { RegistrySchema } from "@/services/registries/registriesAPI";

export class MeasurementValueCoercer {
    private readonly globalRelationOptions: RelationOptions[];
    private readonly entryRelationOptions: RelationOptions[];

    static buildForExperiment(
        protocol: AbstractSchemaConfigWithGlobalRelationOptions,
        experiment: Experiment
    ) {
        const entryRelationOptions = experiment.entryRelationOptions;
        const globalRelationOptions = protocol.globalRelationOptions;
        return new MeasurementValueCoercer(globalRelationOptions, entryRelationOptions);
    }

    static buildForRegistrySchema(registrySchema: RegistrySchema) {
        return new MeasurementValueCoercer(registrySchema.globalRelationOptions, []);
    }

    constructor(globalRelationOptions: RelationOptions[], entryRelationOptions: RelationOptions[]) {
        this.globalRelationOptions = globalRelationOptions;
        this.entryRelationOptions = entryRelationOptions;
    }

    getMeasurementDisplayValue(attribute: Attribute, measurementValue: unknown): unknown {
        if (_.isNil(measurementValue)) {
            measurementValue = attribute.option_default;
        }

        if (_.isNil(measurementValue)) {
            return measurementValue;
        }

        switch (attribute.type) {
            case "number":
                return toDisplayValue(measurementValue, (value) => {
                    // This is largely because default values for number attributes get saved as
                    // strings instead of numbers.
                    if (_.isString(value)) {
                        return parseNumber(value);
                    } else if (_.isNumber(value)) {
                        return value;
                    } else {
                        // unsure how to parse whatever this is as a number...
                        return value;
                    }
                });
            case "boolean":
                if (_.isBoolean(measurementValue)) {
                    return measurementValue as boolean;
                } else {
                    return measurementValue === "true";
                }
            case "number_array":
            case "string_array":
                if (_.isArray(measurementValue)) {
                    return measurementValue.join(", ");
                }
                return measurementValue as string;
            case "global_relation":
                return toDisplayValue(measurementValue, (value) => {
                    return getRelationOptionDisplayName(
                        this.globalRelationOptions,
                        attribute,
                        value
                    );
                });
            case "entry_relation":
                return toDisplayValue(measurementValue, (value) => {
                    return getRelationOptionDisplayName(
                        this.entryRelationOptions,
                        attribute,
                        value
                    );
                });
            default:
                return toDisplayValue(measurementValue, (value) => {
                    return value;
                });
        }
    }
}

function toDisplayValue<T>(savedValue: unknown, coercerFn: (savedValue: unknown) => T) {
    if (_.isArray(savedValue)) {
        return savedValue.map(coercerFn).join(", ");
    }
    return coercerFn(savedValue);
}
